import * as THREE from "three";
import { createRng } from "@shared/game";
import { SANDBOX_CONFIG } from "./sandboxConfig";

interface Sample {
  s: number; // arc length from start
  pos: THREE.Vector3;
  tan: THREE.Vector3; // unit tangent in XZ plane
}

interface PlatformStep {
  start: number;
  rampUpEnd: number;
  topEnd: number;
  dropEnd: number;
  height: number;
}

interface NarrowBridgeSection {
  center: number;
  start: number;
  end: number;
  halfWidth: number;
}

/**
 * Curved running path. Built from a CatmullRom spline through random
 * waypoints (seeded). Provides:
 *   - positionAt(s, laneOffset) — world position at arc length `s`, offset
 *     perpendicular to the tangent by `laneOffset` (lane swing).
 *   - tangentAt(s) — unit tangent direction at `s`.
 *   - yawAt(s) — Y-axis rotation matching the tangent (for orienting meshes).
 *
 * Players, obstacles, coins and gates all live along this curve via their
 * `s` value; the world bends around them and the camera follows naturally.
 */
export class Path {
  private samples: Sample[] = [];
  private platforms: PlatformStep[] = [];
  private narrowBridgeSectionsCache: NarrowBridgeSection[] | null = null;
  public length: number = 0;
  /** Approximate arc-length positions of each corner — used by Spawner to avoid
   *  placing obstacles right on a turn (can't see them around the bend). */
  public cornerS: number[] = [];

  /** True if `s` is within `radius` of any corner. */
  isNearCorner(s: number, radius = 8): boolean {
    for (const cs of this.cornerS) if (Math.abs(cs - s) < radius) return true;
    return false;
  }

  narrowBridgeSections(): NarrowBridgeSection[] {
    if (this.narrowBridgeSectionsCache) return this.narrowBridgeSectionsCache;
    const length = 34;
    const halfWidth = 2.35;
    const candidates = [0.54, 0.46, 0.64].map((t) => this.length * t);
    let center = candidates[0]!;
    for (const c of candidates) {
      if (!this.isNearCorner(c, 32) && !this.isNearCorner(c - length / 2, 18) && !this.isNearCorner(c + length / 2, 18)) {
        center = c;
        break;
      }
    }
    this.narrowBridgeSectionsCache = [{
      center,
      start: Math.max(42, center - length / 2),
      end: Math.min(this.length - 52, center + length / 2),
      halfWidth,
    }];
    return this.narrowBridgeSectionsCache;
  }

  narrowBridgeHalfWidthAt(s: number): number | null {
    const section = this.narrowBridgeSections().find((b) => s >= b.start && s <= b.end);
    return section ? section.halfWidth : null;
  }

  /** Door-dash floor half-width at arc length `s`. Linearly interpolates
   *  between consecutive door rows so the fall threshold tapers with the
   *  visible ground — the player can't fly past the doors in the air. */
  doorDashFloorHalfWidthAt(s: number): number | null {
    const dd = this.doorDash;
    if (!dd) return null;
    const CELL_WIDTH = 1.7;
    const rowCount = dd.doorRows.length;
    const rowHalf = (r: number) => (Math.max(1, rowCount - r) * CELL_WIDTH) / 2;
    // Build the same control points the ground mesh uses.
    const pts: { s: number; halfW: number }[] = [];
    pts.push({ s: 0, halfW: dd.halfWidth });
    pts.push({ s: Math.max(0, dd.doorRows[0]! - 4), halfW: dd.halfWidth });
    for (let r = 0; r < rowCount; r++) {
      pts.push({ s: dd.doorRows[r]!, halfW: rowHalf(r) });
    }
    pts.push({ s: this.length, halfW: rowHalf(rowCount - 1) });
    if (s <= pts[0]!.s) return pts[0]!.halfW;
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1]!;
      const b = pts[i]!;
      if (s <= b.s) {
        const t = (s - a.s) / Math.max(0.0001, b.s - a.s);
        return a.halfW + (b.halfW - a.halfW) * t;
      }
    }
    return pts[pts.length - 1]!.halfW;
  }

  /** Set when the course is built in "hit-parade" mode — a straight ramp with no corners. */
  hitParade: {
    flatStart: number;
    rampEnd: number;
    flatEnd: number;
    rampHeight: number;
    /** Stretch of the path with no floor — player must jump on the moving
     *  platform to cross it. World tile renderer skips this range; Game's
     *  fall-off check pushes players into a fall when in this range without
     *  platform support. */
    gapStart: number;
    gapEnd: number;
  } | null = null;

  /** Set when the course is built in "ice-crack" mode — a straight flat track
   *  with multiple no-floor ice-tile ranges that the player must traverse via
   *  cracking tiles. Each range corresponds to one ice-tile field. */
  iceCrack: {
    /** Stretches with no inherent floor — the player only stays up while
     *  standing on an un-cracked ice tile. Outside these ranges the track is
     *  a solid platform. */
    fields: { start: number; end: number }[];
    /** Half-width of the track (lateral fall-off cutoff). */
    halfWidth: number;
  } | null = null;

  /** Set when the course is built in "train-roof" mode. THREE parallel lanes
   *  of boxcars (left / centre / right) that bob up and down out of phase —
   *  the player runs across the rooftops, riding whichever train is under
   *  their feet and hopping between lanes. Decor builds the cars/signs/tunnels;
   *  only the layout lives here. */
  trainRoof: {
    /** Solid roof segments along each lane (player supported here; gaps
     *  between cars and between lanes). Shared s-ranges across all lanes. */
    cars: { start: number; end: number }[];
    /** The three lanes: lateral offset, bob phase, and bob amplitude. */
    lanes: { offset: number; phase: number; amp: number }[];
    /** s-positions of the challenge gates (on the centre lane). */
    gates: number[];
    /** Swinging-sign s-positions (centre lane). */
    signs: number[];
    /** Low-tunnel arch s-positions (a crate to jump sits just before). */
    tunnels: number[];
    /** Smoke-cloud s-positions (visibility decor). */
    smoke: number[];
    /** Half-width of one lane's boxcar roof (lateral fall-off per lane). */
    halfWidth: number;
  } | null = null;

  /** Set when the course is built in "door-dash" mode. Stores the s-positions
   *  of each door row + each checkpoint gate so iceCrack-style decor logic
   *  doesn't have to be duplicated and Game knows where to mount what. */
  doorDash: {
    /** Where each row of doors lives (arc-length center). */
    doorRows: number[];
    /** Where each spinner sits. */
    spinners: number[];
    /** Where each checkpoint gate sits. */
    gates: number[];
    /** Half-width of the playable track. */
    halfWidth: number;
  } | null = null;

  /** Set when the course is built in "rolling-tubes" mode. The track is a
   *  sequence of solid platforms separated by horizontally-rotating cylinders
   *  the player must run across. */
  rollingTubes: {
    /** Solid platform ranges (player always supported here). Each
     *  platform sits at a fixed `y` — alternating high/low so the
     *  course steps UP and DOWN as the player progresses. */
    platforms: { start: number; end: number; y: number }[];
    /** Spinning tube ranges — only supported on top of the cylinder.
     *  Each tube is a sloped ramp connecting consecutive platforms;
     *  `slide: true` marks the down-ramp where the player slides. */
    tubes: {
      start: number;
      end: number;
      radius: number;
      angularVel: number;
      startY: number;
      endY: number;
      slide: boolean;
    }[];
    halfWidth: number;
  } | null = null;

  /** Set when the course is built in "circle-spinner" mode. A straight track
   *  laid out as five circular spinner platforms connected by short corridors
   *  with checkpoint gates between them. */
  circleSpinner: {
    /** Round spinner platforms (always-supported areas the player can stand on). */
    circles: { center: number; halfDepth: number; radius: number; angularVel: number }[];
    /** Corridor ranges between circles — also solid floor. */
    corridors: { start: number; end: number }[];
    /** Gate s-positions, one per corridor between circles. */
    gates: number[];
    /** Track half-width outside circles (corridors). */
    corridorHalfWidth: number;
  } | null = null;

  /** Set when the course is built in "maze-rush" mode. Two sections of three
   *  floating curved-ribbon lanes that diverge / cross / reconverge between
   *  full-width platform pads. No side walls — drift off the lane edge and
   *  you fall. Lane shapes are encoded as analytic profiles per (section,
   *  lane) — see mazeRushLaneWarpAt for the curve formula. */
  mazeRush: {
    /** Solid platform spans (start, mid, finish). Flat at y=0.18. */
    platforms: { start: number; end: number }[];
    /** Curved-lane section spans. Three lanes weave through each one. */
    sections: { start: number; end: number }[];
    /** Lane endpoint x positions on the platforms (where lanes meet the pad).
     *  Lane 0 = left, 1 = center, 2 = right. */
    laneEndX: number[];
    /** Lane ribbon half-width (lateral hit-zone around the lane curve). */
    laneHalfWidth: number;
    /** Number of lanes per section. */
    laneCount: number;
    /** Gate s-positions (one per platform with a checkpoint gate). */
    gates: number[];
  } | null = null;

  /** Set when the course is built in "supermarket-sweep" mode. Straight
   *  aisle with shelf walls left & right, a sequence of cracked-floor
   *  patches, slippery spill zones, holes, and 3 word gates. */
  supermarketSweep: {
    /** Total course length (same as path.length, mirrored here for clarity). */
    length: number;
    /** Half-width of the walkable aisle (between the shelves). */
    halfWidth: number;
    /** S-ranges of cracked-floor patches. Each cell inside breaks when
     *  the player steps on it (re-using the ice-crack mechanic). */
    crackedZones: { start: number; end: number }[];
    /** S-ranges of slippery patches. Player gets lateral push while
     *  standing in one. Direction +1 = push to +X, -1 = push to -X. */
    slipperyZones: { start: number; end: number; dir: 1 | -1 }[];
    /** Hole spans — no floor in this s-range, falls through. */
    holes: { start: number; end: number; lateralFrom: number; lateralTo: number }[];
    /** Gate s-positions (3 word gates). */
    gates: number[];
    /** Cannon-like falling-product positions (shelf side at this s). */
    productCannons: { s: number; side: 1 | -1 }[];
  } | null = null;

  /** Set when the course is built in "river-boat" mode. Three sections
   *  of moving boats separated by safe wooden docks, with fish-jump
   *  hazards in the middle section. Start + finish docks are solid. */
  riverBoat: {
    /** Solid wooden docks (start / mid pads / finish). */
    docks: { start: number; end: number }[];
    /** River-gap spans the player crosses via moving boats. */
    gaps: { start: number; end: number }[];
    /** Fish-jump anchor positions (s + side: -1 left, +1 right). */
    fish: { s: number; side: 1 | -1 }[];
    /** Word-gate s-positions (one per non-start dock). */
    gates: number[];
    /** Track lateral half-width. */
    halfWidth: number;
  } | null = null;

  /** Set when the course is built in "block-climb" mode. Three climb
   *  ramps + two safe pads + a finish plateau. The path's heightAt
   *  follows the ramp/pad profile so the player walks up smoothly;
   *  visual stair-block decoration is added by the decor builder. */
  blockClimb: {
    /** Total course length. */
    length: number;
    /** Half-width of the playable corridor. */
    halfWidth: number;
    /** Climb sections — heightAt rises smoothly from startY to endY
     *  across each `start..end` s-range. */
    climbs: { start: number; end: number; startY: number; endY: number }[];
    /** Safe pads (flat) where word gates sit. */
    pads: { start: number; end: number; y: number }[];
    /** S-positions of the 3 word gates (one per safe pad + finish). */
    gates: number[];
    /** Ball-cannon visual positions (s + lateral). */
    cannons: { s: number; l: number }[];
  } | null = null;

  /** Returns the lane warp (lateral dx, vertical dy) at arc length `s` for
   *  the given lane index in maze-rush mode. Returns zero on platforms (so
   *  the player lands flat) and curves smoothly within each section. */
  mazeRushLaneWarpAt(laneIdx: number, s: number): { dx: number; dy: number } {
    const mr = this.mazeRush;
    if (!mr) return { dx: 0, dy: 0 };
    // Find section containing s.
    let secIdx = -1;
    let t = 0;
    for (let i = 0; i < mr.sections.length; i++) {
      const sec = mr.sections[i]!;
      if (s >= sec.start && s <= sec.end) {
        secIdx = i;
        t = (s - sec.start) / (sec.end - sec.start);
        break;
      }
    }
    if (secIdx < 0) return { dx: 0, dy: 0 };
    return laneWarp(secIdx, laneIdx, t);
  }

  /** Lane lateral half-width (for fall-off + clamp). */
  mazeRushLaneHalfWidth(): number {
    return this.mazeRush?.laneHalfWidth ?? 1.4;
  }

  /** True if `s` sits on a platform pad (no lane warp applies). */
  mazeRushOnPlatform(s: number): boolean {
    const mr = this.mazeRush;
    if (!mr) return false;
    return mr.platforms.some((p) => s >= p.start && s <= p.end);
  }

  /** Returns the active section index containing `s`, or -1 if on a platform. */
  mazeRushSectionIdx(s: number): number {
    const mr = this.mazeRush;
    if (!mr) return -1;
    for (let i = 0; i < mr.sections.length; i++) {
      const sec = mr.sections[i]!;
      if (s >= sec.start && s <= sec.end) return i;
    }
    return -1;
  }

  /** Set when the course is built in "space-ship" mode. A line of fixed
   *  space-station islands separated by void gaps; the player crosses each
   *  gap by riding a slow-moving spaceship platform. Some islands carry a
   *  spinning bar obstacle. */
  spaceShip: {
    /** Solid island spans (always-supported). */
    islands: { start: number; end: number }[];
    /** Void gaps between islands — no floor unless on a moving platform. */
    gaps: { start: number; end: number }[];
    /** Spinner positions (s-axis), with bar radius + angular velocity. */
    spinners: { s: number; radius: number; angularVel: number }[];
    /** Gate s-positions (one per gated island). */
    gates: number[];
    /** Track lateral half-width (lateral fall-off cutoff). */
    halfWidth: number;
  } | null = null;

  constructor(targetLength: number, seed: number, mode: "default" | "hit-parade" | "ice-crack" | "door-dash" | "rolling-tubes" | "circle-spinner" | "maze-rush" | "space-ship" | "supermarket-sweep" | "block-climb" | "river-boat" | "double-spinner" | "train-roof" = "default") {
    if (mode === "hit-parade") {
      this.buildHitParade(targetLength);
      return;
    }
    if (mode === "ice-crack") {
      this.buildIceCrack(targetLength);
      return;
    }
    if (mode === "door-dash") {
      this.buildDoorDash(targetLength);
      return;
    }
    if (mode === "rolling-tubes") {
      this.buildRollingTubes(targetLength);
      return;
    }
    if (mode === "circle-spinner") {
      this.buildCircleSpinner(targetLength);
      return;
    }
    if (mode === "double-spinner") {
      this.buildDoubleSpinner();
      return;
    }
    if (mode === "maze-rush") {
      this.buildMazeRush(targetLength);
      return;
    }
    if (mode === "space-ship") {
      this.buildSpaceShip(targetLength);
      return;
    }
    if (mode === "supermarket-sweep") {
      this.buildSupermarketSweep(targetLength);
      return;
    }
    if (mode === "block-climb") {
      this.buildBlockClimb(targetLength);
      return;
    }
    if (mode === "river-boat") {
      this.buildRiverBoat(targetLength);
      return;
    }
    if (mode === "train-roof") {
      this.buildTrainRoof(targetLength);
      return;
    }
    const rng = createRng(seed);
    const points: THREE.Vector3[] = [];
    // Pre-roll for a clean tangent at s=0.
    points.push(new THREE.Vector3(0, 0, -20));
    points.push(new THREE.Vector3(0, 0, -10));

    // Walk forward by tracking a heading angle (0 = +Z). Periodically apply
    // a sharp turn so the course actually has corners (Temple Run-style)
    // instead of just gentle sideways drift.
    const pos = new THREE.Vector3(0, 0, 0);
    let heading = 0;
    let s = 0;

    // Opening straight — give the player ~30m to orient before the first turn.
    while (s < 30) {
      pos.z += 8;
      s += 8;
      points.push(pos.clone());
    }

    // Cornered section. ~30% of waypoints kick a real corner (35-80°),
    // the rest are straight steps. Two corners can't appear back-to-back
    // (gives the player a beat to recover and lets obstacle spawning leave
    // a clean buffer around each turn).
    let lastTurnSign = 0;
    let stepsSinceCorner = 99;
    this.cornerS = [];
    while (s < targetLength - 30) {
      const wantCorner = stepsSinceCorner >= 2 && rng() < 0.3;
      if (wantCorner) {
        let sign = rng() < 0.5 ? -1 : 1;
        // Avoid two same-direction corners in close succession (looks like a loop).
        if (stepsSinceCorner < 4 && sign === lastTurnSign) sign = -sign;
        const turnDeg = 35 + rng() * 45;
        heading += ((turnDeg * Math.PI) / 180) * sign;
        lastTurnSign = sign;
        stepsSinceCorner = 0;
        // Mark the corner's arc position so Spawner can avoid it.
        this.cornerS.push(s);
        // Tighter post-corner point so the curve hugs the bend instead of
        // straight-lining diagonally between waypoints.
        const tight = 5;
        pos.x += Math.sin(heading) * tight;
        pos.z += Math.cos(heading) * tight;
        s += tight;
        points.push(pos.clone());
      }
      const stepDist = 18 + rng() * 10;
      pos.x += Math.sin(heading) * stepDist;
      pos.z += Math.cos(heading) * stepDist;
      s += stepDist;
      points.push(pos.clone());
      stepsSinceCorner++;
    }

    // Final straight runway so the finish line is dead ahead.
    for (let i = 0; i < 2; i++) {
      pos.x += Math.sin(heading) * 14;
      pos.z += Math.cos(heading) * 14;
      points.push(pos.clone());
    }

    // `centripetal` parameterization avoids loops/cusps at sharp corners and
    // hugs the points tighter than the default `catmullrom`.
    const curve = new THREE.CatmullRomCurve3(points, false, "centripetal", 0.5);

    // Resample by arc length for fast position lookup.
    const SAMPLES = 500;
    const raw: THREE.Vector3[] = [];
    for (let i = 0; i <= SAMPLES; i++) raw.push(curve.getPoint(i / SAMPLES));
    let len = 0;
    let prev = raw[0]!;
    this.samples.push({ s: 0, pos: prev.clone(), tan: this.tangentBetween(raw[0]!, raw[1]!) });
    for (let i = 1; i <= SAMPLES; i++) {
      const p = raw[i]!;
      len += p.distanceTo(prev);
      const next = raw[Math.min(SAMPLES, i + 1)]!;
      this.samples.push({ s: len, pos: p.clone(), tan: this.tangentBetween(prev, next) });
      prev = p;
    }
    this.length = len;
    this.platforms = this.buildPlatforms(targetLength, seed);
    // Trim arc length so the requested target lines up with the spline end —
    // we slightly overshoot above for the runway.
  }

  /**
   * Hit Parade course: dead-straight track with a flat starting lobby, a smooth
   * uphill ramp where bouncing balls roll down at the player, and a flat top
   * with the finish line.
   */
  private buildHitParade(targetLength: number) {
    const length = Math.max(140, targetLength);
    const flatStart = 30;
    const rampEnd = length - 25; // most of the course is ramp
    const flatEnd = length;
    const rampHeight = 22; // tall hill — makes falling off feel real
    // No-op gap (start === end). Level 1 originally had a moving platform
    // bridging a real gap here, but the platform mechanic was too punishing
    // for the very first level — it's been removed. Keeping zero-width fields
    // so we can put it back on a later level just by tweaking these numbers.
    const gapStart = 0;
    const gapEnd = 0;
    this.hitParade = { flatStart, rampEnd, flatEnd, rampHeight, gapStart, gapEnd };
    this.platforms = []; // no random platforms; height comes from hitParade
    this.cornerS = [];
    this.narrowBridgeSectionsCache = [];
    // Build a dense, straight set of samples along +Z so positionAt is exact.
    const STEP = 1.0;
    const tangent = new THREE.Vector3(0, 0, 1);
    for (let s = 0; s <= length; s += STEP) {
      this.samples.push({
        s,
        pos: new THREE.Vector3(0, 0, s),
        tan: tangent.clone(),
      });
    }
    if (this.samples[this.samples.length - 1]!.s < length) {
      this.samples.push({ s: length, pos: new THREE.Vector3(0, 0, length), tan: tangent.clone() });
    }
    this.length = length;
  }

  /** Ice-Crack course: dead-straight flat track. Six "ice tile field" stretches
   *  alternate with solid platforms holding a spinner + checkpoint gate. The
   *  ice fields are no-floor zones — the player only stays up while standing
   *  on an un-cracked tile. Decoration + tile cracking is owned by iceCrackDecor. */
  private buildTrainRoof(targetLength: number) {
    // Longer course (was 300) so the rooftop run reads as a proper journey.
    const length = Math.max(460, targetLength);
    const halfWidth = 2.1;          // per-lane roof half-width
    const laneSep = 5.6;            // lateral spacing between lane centres
    // Three lanes bobbing up and down, out of phase. The centre lane (the main
    // path with the gates) bobs gently; the side trains swing more.
    const lanes = [
      { offset: -laneSep, phase: 0.0, amp: 1.35 },
      { offset: 0, phase: 2.1, amp: 0.6 },
      { offset: laneSep, phase: 4.2, amp: 1.35 },
    ];

    // Layout: starting-station roof → a long chain of boxcars separated by
    // gaps (one extra-wide "separating cars" gap) → finish station.
    const startStation = 28;
    const finishStation = 28;
    const usable = length - startStation - finishStation;
    const CAR_COUNT = 10;
    const GAP = 3.0;               // normal coupler gap (jumpable)
    const WIDE_GAP = 5.4;          // the "separating cars" gap
    const carLen = (usable - GAP * (CAR_COUNT - 1)) / CAR_COUNT;

    const cars: { start: number; end: number }[] = [];
    cars.push({ start: 0, end: startStation });
    let cursor = startStation;
    const carMid: number[] = [];
    for (let i = 0; i < CAR_COUNT; i++) {
      cursor += i === 5 ? WIDE_GAP : GAP; // gap before each car (one is wide)
      const cStart = cursor;
      const cEnd = cStart + carLen;
      cars.push({ start: cStart, end: cEnd });
      carMid.push((cStart + cEnd) / 2);
      cursor = cEnd;
    }
    cars.push({ start: cursor + GAP, end: length });

    // 5 gates spread along the chain (skip the car right after the wide gap).
    const gates = [carMid[0]!, carMid[2]!, carMid[4]!, carMid[7]!, carMid[9]!];
    const signs = [
      carMid[1]!, carMid[3]!, carMid[6]!, carMid[8]!,
    ];
    const tunnels = [carMid[2]!, carMid[7]!];
    const smoke = [carMid[5]!];

    this.trainRoof = { cars, lanes, gates, signs, tunnels, smoke, halfWidth };
    this.platforms = [];
    this.cornerS = [];
    this.narrowBridgeSectionsCache = [];
    const STEP = 1.0;
    const tangent = new THREE.Vector3(0, 0, 1);
    for (let s = 0; s <= length; s += STEP) {
      this.samples.push({ s, pos: new THREE.Vector3(0, 0, s), tan: tangent.clone() });
    }
    if (this.samples[this.samples.length - 1]!.s < length) {
      this.samples.push({ s: length, pos: new THREE.Vector3(0, 0, length), tan: tangent.clone() });
    }
    this.length = length;
  }

  private buildIceCrack(targetLength: number) {
    const length = Math.max(140, targetLength);
    const fields: { start: number; end: number }[] = [];
    // Layout (per segment, ~38u):
    //   ice field (22u)  →  spinner platform (12u)  →  gate platform (4u)
    // Start platform = 22u, then 6 segments, then a finish platform.
    const startSolid = 22;
    const segments = 6;
    const segLen = (length - startSolid - 10) / segments; // 10u for finish platform
    for (let i = 0; i < segments; i++) {
      const segStart = startSolid + i * segLen;
      const iceStart = segStart;
      const iceEnd = segStart + segLen * 0.6; // ~22u of ice if seg=38
      fields.push({ start: iceStart, end: iceEnd });
      // Remaining seg ~16u is solid spinner+gate platform.
    }
    this.iceCrack = { fields, halfWidth: 5.5 };
    this.platforms = [];
    this.cornerS = [];
    this.narrowBridgeSectionsCache = [];
    const STEP = 1.0;
    const tangent = new THREE.Vector3(0, 0, 1);
    for (let s = 0; s <= length; s += STEP) {
      this.samples.push({ s, pos: new THREE.Vector3(0, 0, s), tan: tangent.clone() });
    }
    if (this.samples[this.samples.length - 1]!.s < length) {
      this.samples.push({ s: length, pos: new THREE.Vector3(0, 0, length), tan: tangent.clone() });
    }
    this.length = length;
  }

  /** Door-Dash course: dead-straight flat track with the following pattern,
   *  spread across the length:
   *    start pad  →  5 × [ door row → spinner → gate ]  →  finish pad
   *  All s-positions are precomputed here so Spawner can place gates and
   *  doorDashDecor can place doors / spinners at the same offsets. */
  private buildDoorDash(targetLength: number) {
    const length = Math.max(220, targetLength);
    const startPad = 22;
    const finishPad = 16;
    const usable = length - startPad - finishPad;
    // 8 door rows spaced evenly along the usable stretch — the road narrows
    // row by row (8 → 7 → 6 → … → 1 doors). Spinners + checkpoint gates
    // still come from a 5-station segmentation so the lesson stays at 5
    // questions.
    const doorRowCount = 8;
    const doorRows: number[] = [];
    for (let i = 0; i < doorRowCount; i++) {
      const t = (i + 1) / (doorRowCount + 1);
      doorRows.push(startPad + usable * t);
    }
    // Spinners + gates go in the GAPS BETWEEN consecutive door rows so they
    // never overlap a row. There are (doorRowCount + 1) such gaps. We pick
    // 5 evenly-spaced gaps for gates and a different 4 for spinners so the
    // two don't stack either.
    const gapMidpoints: number[] = [];
    gapMidpoints.push((startPad + doorRows[0]!) / 2);
    for (let i = 0; i < doorRowCount - 1; i++) {
      gapMidpoints.push((doorRows[i]! + doorRows[i + 1]!) / 2);
    }
    gapMidpoints.push((doorRows[doorRowCount - 1]! + (startPad + usable)) / 2);
    // 9 gaps → spinners in odd-indexed (0, 2, 4, 6), gates in even-indexed
    // (1, 3, 5, 7, 8) so spinners and gates alternate cleanly.
    const spinners: number[] = [];
    const gates: number[] = [];
    for (let i = 0; i < gapMidpoints.length; i++) {
      if (i === 0 || i === 2 || i === 4 || i === 6) spinners.push(gapMidpoints[i]!);
      else gates.push(gapMidpoints[i]!);
    }
    this.doorDash = { doorRows, spinners, gates, halfWidth: 5.5 };
    this.platforms = [];
    this.cornerS = [];
    this.narrowBridgeSectionsCache = [];
    const STEP = 1.0;
    const tangent = new THREE.Vector3(0, 0, 1);
    for (let s = 0; s <= length; s += STEP) {
      this.samples.push({ s, pos: new THREE.Vector3(0, 0, s), tan: tangent.clone() });
    }
    if (this.samples[this.samples.length - 1]!.s < length) {
      this.samples.push({ s: length, pos: new THREE.Vector3(0, 0, length), tan: tangent.clone() });
    }
    this.length = length;
  }

  /** Rolling-Tubes course: dead-straight flat track laid out as
   *    start pad → [platform-with-gate, spinning tube] × 5 → finish pad
   *  Solid platforms support the player normally; tube ranges have no inherent
   *  floor — rollingTubesDecor's hasTubeSupport check decides whether the
   *  player is still on the cylinder's top band.
   */
  private buildRollingTubes(targetLength: number) {
    // PYRAMID rebuild (Level 3) — a giant stair-stepped ziggurat. The
    // player jumps up each step. Spinners sit on some of the middle
    // steps, and balls roll down from the top. Step rise is tuned so
    // a normal jump JUST clears it — every step is a small commitment.
    const STEP_COUNT = 14;       // more steps, taller pyramid
    const stepDepth = 9;
    const stepRise = 1.4;
    const baseY = 0.18;
    // Path length EXACTLY covers the pyramid — ignore targetLength so
    // the engine's finish trigger fires at the top of the climb, not
    // at some imaginary point far beyond the last step.
    void targetLength;
    // Path length lines up exactly with the summit so the engine's
    // finish trigger fires the moment the player crosses the arch.
    const length = STEP_COUNT * stepDepth;
    const platforms: { start: number; end: number; y: number }[] = [];
    for (let i = 0; i < STEP_COUNT; i++) {
      platforms.push({
        start: i * stepDepth,
        end: (i + 1) * stepDepth,
        y: baseY + i * stepRise,
      });
    }
    // No tubes in the new layout — every section is a solid step.
    const tubes: { start: number; end: number; radius: number; angularVel: number; startY: number; endY: number; slide: boolean }[] = [];
    this.rollingTubes = { platforms, tubes, halfWidth: 5.5 };
    this.platforms = [];
    this.cornerS = [];
    this.narrowBridgeSectionsCache = [];
    const STEP = 1.0;
    const tangent = new THREE.Vector3(0, 0, 1);
    for (let s = 0; s <= length; s += STEP) {
      this.samples.push({ s, pos: new THREE.Vector3(0, 0, s), tan: tangent.clone() });
    }
    if (this.samples[this.samples.length - 1]!.s < length) {
      this.samples.push({ s: length, pos: new THREE.Vector3(0, 0, length), tan: tangent.clone() });
    }
    this.length = length;
  }

  /** Circle-Spinner course: dead-straight flat track laid out as
   *    start pad → [circle, corridor-with-gate] × (5 circles, 4 gates) → finish
   *  Survival arena — a single big round platform with two opposing spinner
   *  bars on a central pillar. There is no forward progress: the path length
   *  is set to a small value (`ARENA_SPAN`) and the arena occupies the middle
   *  of it. Players stand at s=ARENA_SPAN/2 and dodge the bars. The decor is
   *  built by `doubleSpinnerArena.ts`. */
  private buildDoubleSpinner() {
    // Survival arena: a single circle of radius 12 centred at s=ARENA_SPAN/2.
    // The path is a degenerate straight stretch — no forward progress, no
    // gates. The platform itself comes from `doubleSpinnerArena.ts`.
    const ARENA_SPAN = 30;
    const ARENA_RADIUS = 12;
    this.platforms = [];
    this.cornerS = [];
    this.narrowBridgeSectionsCache = [];
    const STEP = 1.0;
    const tangent = new THREE.Vector3(0, 0, 1);
    for (let s = 0; s <= ARENA_SPAN; s += STEP) {
      this.samples.push({ s, pos: new THREE.Vector3(0, 0, s), tan: tangent.clone() });
    }
    if (this.samples[this.samples.length - 1]!.s < ARENA_SPAN) {
      this.samples.push({ s: ARENA_SPAN, pos: new THREE.Vector3(0, 0, ARENA_SPAN), tan: tangent.clone() });
    }
    this.length = ARENA_SPAN;
    // Re-use circleSpinner's single-circle data shape so existing support /
    // collision plumbing can read it. The arena is one big circle.
    this.circleSpinner = {
      circles: [
        { center: ARENA_SPAN / 2, halfDepth: ARENA_RADIUS, radius: ARENA_RADIUS, angularVel: 0 },
      ],
      corridors: [],
      gates: [],
      corridorHalfWidth: ARENA_RADIUS,
    };
  }

  /**
   *  Circles are large round platforms with a long bar spinning across them;
   *  corridors are narrow walkways with a checkpoint gate near the end.
   */
  private buildCircleSpinner(targetLength: number) {
    const length = Math.max(180, targetLength);
    const startPad = 14;
    const finishPad = 14;
    const usable = length - startPad - finishPad;
    const circleCount = 5;
    const gateCount = circleCount - 1; // 4 gates between 5 circles
    // halfDepth = radius so the path's "this s is on the circle" range matches
    // the visible round footprint exactly. Avoids the dead-space gap between
    // the round edge and the rectangular corridor.
    const circleRadius = 8.5;
    const reservedCircles = circleCount * circleRadius * 2;
    const corridorDepth = Math.max(10, (usable - reservedCircles) / gateCount);
    const circles: { center: number; halfDepth: number; radius: number; angularVel: number }[] = [];
    const corridors: { start: number; end: number }[] = [];
    const gates: number[] = [];
    let cursor = startPad;
    for (let i = 0; i < circleCount; i++) {
      const circleStart = cursor;
      const circleCenter = circleStart + circleRadius;
      const dir = i % 2 === 0 ? 1 : -1;
      circles.push({
        center: circleCenter,
        halfDepth: circleRadius,
        radius: circleRadius,
        // Slower bars — original 2.4 rad/s was too quick to dodge cleanly on
        // a level meant to teach the rhythm. 1.3 gives a comfortable beat.
        angularVel: dir * 1.3,
      });
      cursor = circleStart + circleRadius * 2;
      // Corridor + gate after every circle except the last.
      if (i < circleCount - 1) {
        const corStart = cursor;
        const corEnd = corStart + corridorDepth;
        corridors.push({ start: corStart, end: corEnd });
        gates.push(corStart + corridorDepth * 0.55);
        cursor = corEnd;
      }
    }
    this.circleSpinner = { circles, corridors, gates, corridorHalfWidth: 2.4 };
    this.platforms = [];
    this.cornerS = [];
    this.narrowBridgeSectionsCache = [];
    const STEP = 1.0;
    const tangent = new THREE.Vector3(0, 0, 1);
    for (let s = 0; s <= length; s += STEP) {
      this.samples.push({ s, pos: new THREE.Vector3(0, 0, s), tan: tangent.clone() });
    }
    if (this.samples[this.samples.length - 1]!.s < length) {
      this.samples.push({ s: length, pos: new THREE.Vector3(0, 0, length), tan: tangent.clone() });
    }
    this.length = length;
  }

  /** Maze-Rush course: floating-ribbon lanes that twist and cross.
   *    start pad → section A (3 lanes diverge/cross/rejoin) → mid pad (gate)
   *      → section B (3 lanes weave again) → finish pad (gate)
   *  Lane lateral + vertical curves come from `laneWarp(secIdx, laneIdx, t)`.
   */
  private buildMazeRush(targetLength: number) {
    const SECTION_COUNT = 5;
    const sectionLenTarget = 30;
    const padLen = 16;
    const startPad = 20;
    const finishPad = 18;
    const computedLength = startPad + finishPad + sectionLenTarget * SECTION_COUNT + padLen * (SECTION_COUNT - 1);
    const length = Math.max(computedLength, targetLength);
    // Distribute any extra length evenly across the lane sections so the
    // platforms stay tight and the lane choreography has room to breathe.
    const extraPerSection = Math.max(0, (length - computedLength) / SECTION_COUNT);
    const sectionLen = sectionLenTarget + extraPerSection;

    const platforms: { start: number; end: number }[] = [];
    const sections: { start: number; end: number }[] = [];
    const gates: number[] = [];

    platforms.push({ start: 0, end: startPad });
    let cursor = startPad;
    for (let i = 0; i < SECTION_COUNT; i++) {
      const secStart = cursor;
      const secEnd = secStart + sectionLen;
      sections.push({ start: secStart, end: secEnd });
      cursor = secEnd;
      // Add a gate platform after every section. The final pad is wider (finish).
      const isLast = i === SECTION_COUNT - 1;
      const padLength = isLast ? finishPad : padLen;
      const padStart = cursor;
      const padEnd = padStart + padLength;
      platforms.push({ start: padStart, end: padEnd });
      gates.push(padStart + padLength * 0.5);
      cursor = padEnd;
    }

    // Spread the lanes much further apart so the player has room to commit
    // to a path without the neighbouring lanes crowding the camera.
    const laneEndX = [-5.4, 0, 5.4];
    const laneHalfWidth = 1.55;
    this.mazeRush = {
      platforms,
      sections,
      laneEndX,
      laneHalfWidth,
      laneCount: 3,
      gates,
    };

    this.platforms = [];
    this.cornerS = [];
    this.narrowBridgeSectionsCache = [];
    const STEP = 1.0;
    const tangent = new THREE.Vector3(0, 0, 1);
    for (let s = 0; s <= length; s += STEP) {
      this.samples.push({ s, pos: new THREE.Vector3(0, 0, s), tan: tangent.clone() });
    }
    if (this.samples[this.samples.length - 1]!.s < length) {
      this.samples.push({ s: length, pos: new THREE.Vector3(0, 0, length), tan: tangent.clone() });
    }
    this.length = length;
  }

  /** Space-Ship course: 5 fixed island stations + 4 void gaps. Big slow
   *  spaceship platforms shuttle inside each gap; the player has to time
   *  their walk to ride one across. Spinners on islands 2 and 4. */
  private buildSpaceShip(targetLength: number) {
    // Rework: only START and FINISH are solid. The entire middle of the
    // course is void with N sequential "platform stations" — each station
    // is a void gap traversed by a pair of slow moving spaceship platforms
    // (offset phases + opposite lateral lanes) so the player must hop from
    // one moving platform to the next. Word gates float in mid-air at the
    // boundary between stations.
    const STATION_COUNT = 6;
    const startLen = 22;
    const finishLen = 24;
    const stationLen = 30;
    const computed = startLen + finishLen + stationLen * STATION_COUNT;
    const length = Math.max(computed, targetLength);
    const extra = Math.max(0, (length - computed) / STATION_COUNT);
    const stationLenFinal = stationLen + extra;

    const islands: { start: number; end: number }[] = [
      { start: 0, end: startLen },
    ];
    const gaps: { start: number; end: number }[] = [];
    const gates: number[] = [];
    let cursor = startLen;
    for (let i = 0; i < STATION_COUNT; i++) {
      const gStart = cursor;
      const gEnd = gStart + stationLenFinal;
      gaps.push({ start: gStart, end: gEnd });
      cursor = gEnd;
      // 4 floating gates spaced along the middle stations (1..4 of 6).
      // Each gate is anchored to its own station's end so the player has a
      // chance to cross it as they ride the moving platforms in/out.
      if (i >= 1 && i <= 4) gates.push(gStart + stationLenFinal * 0.85);
    }
    islands.push({ start: cursor, end: cursor + finishLen });

    this.spaceShip = {
      islands,
      gaps,
      spinners: [], // no spinners in the rework — focus is platform hops
      gates,
      halfWidth: 5.5,
    };
    this.platforms = [];
    this.cornerS = [];
    this.narrowBridgeSectionsCache = [];
    const STEP = 1.0;
    const tangent = new THREE.Vector3(0, 0, 1);
    for (let s = 0; s <= length; s += STEP) {
      this.samples.push({ s, pos: new THREE.Vector3(0, 0, s), tan: tangent.clone() });
    }
    if (this.samples[this.samples.length - 1]!.s < length) {
      this.samples.push({ s: length, pos: new THREE.Vector3(0, 0, length), tan: tangent.clone() });
    }
    this.length = length;
  }

  /** Supermarket-Sweep (Level 8): straight aisle with shelves on both
   *  sides and a sequence of cracked-floor zones, slippery spill patches,
   *  holes, and product cannons. Word gates sit on safe pads between
   *  hazard sections. */
  private buildSupermarketSweep(targetLength: number) {
    const length = Math.max(220, targetLength);
    const startPad = 22;
    const finishPad = 18;
    const usable = length - startPad - finishPad;
    // Layout knobs — keep zones non-overlapping and leave breathing room
    // for the player to recover between sections.
    const seg = usable / 9; // 9 sub-sections
    const crackedZones: { start: number; end: number }[] = [
      { start: startPad + seg * 1, end: startPad + seg * 2 },           // after first run
      { start: startPad + seg * 4, end: startPad + seg * 5 },           // mid course
    ];
    const slipperyZones: { start: number; end: number; dir: 1 | -1 }[] = [
      { start: startPad + seg * 2.5, end: startPad + seg * 3.5, dir: 1 },
      { start: startPad + seg * 5.5, end: startPad + seg * 6.2, dir: -1 },
    ];
    const holes: { start: number; end: number; lateralFrom: number; lateralTo: number }[] = [
      // First small hole on the left side
      { start: startPad + seg * 6.4, end: startPad + seg * 6.9, lateralFrom: -4.5, lateralTo: -0.6 },
      // Bigger right-side hole later
      { start: startPad + seg * 7.5, end: startPad + seg * 8.0, lateralFrom: 0.4, lateralTo: 4.5 },
    ];
    const gates: number[] = [
      startPad + seg * 2.25,
      startPad + seg * 5.25,
      startPad + seg * 8.5,
    ];
    // Product cannons fire from shelves along the aisle. Spread across
    // the obstacle-heavy first half + the mixed section near the end.
    const productCannons: { s: number; side: 1 | -1 }[] = [
      { s: startPad + seg * 0.5, side: -1 },
      { s: startPad + seg * 0.9, side:  1 },
      { s: startPad + seg * 3.6, side: -1 },
      { s: startPad + seg * 4.2, side:  1 },
      { s: startPad + seg * 6.8, side: -1 },
      { s: startPad + seg * 7.4, side:  1 },
    ];

    this.supermarketSweep = {
      length,
      halfWidth: 5.0,
      crackedZones,
      slipperyZones,
      holes,
      gates,
      productCannons,
    };
    this.platforms = [];
    this.cornerS = [];
    this.narrowBridgeSectionsCache = [];
    const STEP = 1.0;
    const tangent = new THREE.Vector3(0, 0, 1);
    for (let s = 0; s <= length; s += STEP) {
      this.samples.push({ s, pos: new THREE.Vector3(0, 0, s), tan: tangent.clone() });
    }
    if (this.samples[this.samples.length - 1]!.s < length) {
      this.samples.push({ s: length, pos: new THREE.Vector3(0, 0, length), tan: tangent.clone() });
    }
    this.length = length;
  }

  /** Block-Climb (Level 9): straight uphill course built as 3 climb
   *  ramps with 2 flat safe pads in between and a finish plateau at
   *  the top. The physics walks up a smooth profile; visual stair
   *  blocks live in blockClimbDecor. */
  private buildBlockClimb(targetLength: number) {
    const length = Math.max(280, targetLength);
    const startPad = 22;
    const finishPad = 22;
    const usable = length - startPad - finishPad;
    // 5 climbs interleaved with 4 safe pads + finish pad. Five gates,
    // one on each pad / finish — matches the 5 words per lesson.
    const climbLen = usable * 0.14; // each climb
    const padLen = usable * 0.075;  // each interior pad
    let cursor = startPad;
    const climbs: { start: number; end: number; startY: number; endY: number }[] = [];
    const pads: { start: number; end: number; y: number }[] = [];
    const gates: number[] = [];
    const cannons: { s: number; l: number }[] = [];
    let currentY = 0.18;
    const PER_CLIMB_RISE = 4.5;
    const CLIMB_COUNT = 5;
    for (let i = 0; i < CLIMB_COUNT; i++) {
      const cStart = cursor;
      const cEnd = cStart + climbLen;
      climbs.push({ start: cStart, end: cEnd, startY: currentY, endY: currentY + PER_CLIMB_RISE });
      for (const lx of [-3.2, -1, 1, 3.2]) {
        cannons.push({ s: cEnd - 0.3, l: lx });
      }
      currentY += PER_CLIMB_RISE;
      cursor = cEnd;
      if (i < CLIMB_COUNT - 1) {
        const pStart = cursor;
        const pEnd = pStart + padLen;
        pads.push({ start: pStart, end: pEnd, y: currentY });
        gates.push(pStart + padLen * 0.6);
        cursor = pEnd;
      }
    }
    // Finish plateau pad after the last climb — gets the 5th gate.
    pads.push({ start: cursor, end: length, y: currentY });
    gates.push(cursor + (length - cursor) * 0.45);

    this.blockClimb = {
      length,
      halfWidth: 5.2,
      climbs,
      pads,
      gates,
      cannons,
    };
    this.platforms = [];
    // Path bends: turn right after pad 2 (~40% of the course) then turn
    // left after pad 4 (~70%). Stored as `cornerS` so the spawner +
    // camera both pick up the curvature.
    const turnRightS = startPad + usable * 0.42;
    const turnLeftS = startPad + usable * 0.72;
    this.cornerS = [turnRightS, turnLeftS];
    this.narrowBridgeSectionsCache = [];
    const STEP = 1.0;
    // Walk the path forward with a heading that pivots smoothly through
    // each corner. Each turn is ~45° (π/4) blended over a 12m window so
    // the climbs themselves stay straight.
    const TURN_HALF = 6;
    let heading = 0;
    const pos = new THREE.Vector3(0, 0, 0);
    const turns = [
      { s: turnRightS, dir: +1 }, // right
      { s: turnLeftS, dir: -1 },  // left
    ];
    const TURN_TOTAL = (Math.PI / 4); // 45°
    for (let s = 0; s <= length; s += STEP) {
      // Compute target heading by accumulating completed turn fractions.
      let h = 0;
      for (const t of turns) {
        const u = THREE.MathUtils.clamp((s - (t.s - TURN_HALF)) / (TURN_HALF * 2), 0, 1);
        h += t.dir * TURN_TOTAL * u;
      }
      heading = h;
      const tan = new THREE.Vector3(Math.sin(heading), 0, Math.cos(heading));
      this.samples.push({ s, pos: pos.clone(), tan });
      pos.addScaledVector(tan, STEP);
    }
    if (this.samples[this.samples.length - 1]!.s < length) {
      const tan = new THREE.Vector3(Math.sin(heading), 0, Math.cos(heading));
      this.samples.push({ s: length, pos: pos.clone(), tan });
    }
    this.length = length;
  }

  /** River-Boat (Level 8): start dock → 5 gaps of moving boats with
   *  safe wooden mid-docks between them, fish-jump + shark hazards,
   *  finish dock at the end. Same straight-track shape as space-ship
   *  — reuses the moving-platform ride system. 5 gates (one on every
   *  non-start dock) so the lesson matches the 5 words per pack. */
  private buildRiverBoat(targetLength: number) {
    const DOCK_COUNT = 6;       // start + 4 mid + finish
    const GAP_COUNT = DOCK_COUNT - 1;
    const startLen = 22;
    const midLen = 16;
    const finishLen = 22;
    const gapLen = 32;
    const midCount = DOCK_COUNT - 2; // every dock except start/finish
    const computed = startLen + finishLen + midCount * midLen + GAP_COUNT * gapLen;
    const length = Math.max(computed, targetLength);
    const extraPerGap = Math.max(0, (length - computed) / GAP_COUNT);
    const gapLenFinal = gapLen + extraPerGap;

    const docks: { start: number; end: number }[] = [];
    const gaps: { start: number; end: number }[] = [];
    const gates: number[] = [];
    const fish: { s: number; side: 1 | -1 }[] = [];

    docks.push({ start: 0, end: startLen });
    let cursor = startLen;
    for (let i = 0; i < GAP_COUNT; i++) {
      const g = { start: cursor, end: cursor + gapLenFinal };
      gaps.push(g);
      cursor = g.end;
      const isMidDock = i < GAP_COUNT - 1;
      const dLen = isMidDock ? midLen : finishLen;
      const dock = { start: cursor, end: cursor + dLen };
      docks.push(dock);
      // Gate on every dock except the start.
      gates.push(dock.start + dLen * 0.55);
      cursor = dock.end;
    }
    // Fish hazards live in the middle gap (gap index 1).
    const mid = gaps[1]!;
    for (let i = 0; i < 5; i++) {
      const t = (i + 1) / 6;
      fish.push({
        s: mid.start + (mid.end - mid.start) * t,
        side: i % 2 === 0 ? -1 : 1,
      });
    }
    // A couple more fish sprinkled through the final gap for variety.
    const last = gaps[gaps.length - 1]!;
    for (let i = 0; i < 3; i++) {
      const t = (i + 1) / 4;
      fish.push({
        s: last.start + (last.end - last.start) * t,
        side: i % 2 === 0 ? 1 : -1,
      });
    }

    this.riverBoat = { docks, gaps, fish, gates, halfWidth: 5.5 };
    this.platforms = [];
    this.cornerS = [];
    this.narrowBridgeSectionsCache = [];
    const STEP = 1.0;
    const tangent = new THREE.Vector3(0, 0, 1);
    for (let s = 0; s <= length; s += STEP) {
      this.samples.push({ s, pos: new THREE.Vector3(0, 0, s), tan: tangent.clone() });
    }
    if (this.samples[this.samples.length - 1]!.s < length) {
      this.samples.push({ s: length, pos: new THREE.Vector3(0, 0, length), tan: tangent.clone() });
    }
    this.length = length;
  }

  private buildPlatforms(targetLength: number, seed: number): PlatformStep[] {
    const rng = createRng(seed ^ 0x51f15e);
    const platforms: PlatformStep[] = [];
    const first = 115 + rng() * 25;
    const spacing = 170;
    for (let i = 0; i < 4; i++) {
      const start = first + i * spacing + (rng() - 0.5) * 36;
      if (start < 70 || start > targetLength - 95) continue;
      if (this.isNearCorner(start, 24) || this.isNearCorner(start + 55, 24)) continue;
      const ramp = 16 + rng() * 6;
      const top = 28 + rng() * 12;
      const drop = 5.5 + rng() * 3.5;
      const height = 1.6 + rng() * 1.6;
      platforms.push({
        start,
        rampUpEnd: start + ramp,
        topEnd: start + ramp + top,
        dropEnd: start + ramp + top + drop,
        height,
      });
    }
    return platforms;
  }

  private tangentBetween(a: THREE.Vector3, b: THREE.Vector3): THREE.Vector3 {
    const t = b.clone().sub(a);
    t.y = 0;
    return t.normalize();
  }

  private indexFor(s: number): number {
    // Clamp + binary search.
    s = Math.max(0, Math.min(this.length, s));
    let lo = 0;
    let hi = this.samples.length - 1;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      if (this.samples[mid]!.s <= s) lo = mid;
      else hi = mid;
    }
    return lo;
  }

  /** World position at arc length `s`, with optional lateral lane offset (m). */
  positionAt(s: number, laneOffset = 0, target?: THREE.Vector3): THREE.Vector3 {
    const i = this.indexFor(s);
    const a = this.samples[i]!;
    const b = this.samples[i + 1] ?? a;
    const t = b.s > a.s ? (s - a.s) / (b.s - a.s) : 0;
    const out = target ?? new THREE.Vector3();
    out.copy(a.pos).lerp(b.pos, t);
    out.y = this.heightAt(s);
    if (laneOffset !== 0) {
      const tan = this.tangentAt(s, _tmpTan);
      // Perpendicular chosen so that:
      //   1. positive offset visually appears on the player's LEFT (matches
      //      LANE_POSITIONS where higher index = more left), and
      //   2. mesh-local +X (after rotation.y = yaw) lands at the same world
      //      position positionAt would put a positive offset, so multi-lane
      //      obstacles render where their `lanes()` check expects them.
      out.x += tan.z * laneOffset;
      out.z += -tan.x * laneOffset;
    }
    return out;
  }

  heightAt(s: number): number {
    // SANDBOX_CONFIG.floatingLevelBaseY exists as a documented hook for
    // a future world-lift pass. Wiring it in here only would lift the
    // player + path-driven meshes but leave decor modules that use raw
    // Y constants (spaceShip / iceCrack / etc.) at the old altitude —
    // breaking landing maths. Keep the path height local for now; the
    // visible-floor removal + no-respawn refactor already produces the
    // "sky platform" feel.
    return this.heightAtLocal(s);
  }

  /** Local-frame height before any sandbox lift. Every existing per-mode
   *  branch lives in here. */
  private heightAtLocal(s: number): number {
    if (this.hitParade) {
      const { flatStart, rampEnd, rampHeight } = this.hitParade;
      if (s <= flatStart) return 0;
      if (s >= rampEnd) return rampHeight;
      const t = (s - flatStart) / (rampEnd - flatStart);
      return rampHeight * smoothstep(t);
    }
    if (this.iceCrack) {
      // Ice tiles + platforms in iceCrackDecor have their tops near y=0.18.
      // Returning that here keeps both the local player AND remote ghosts on
      // the visible surface (ghosts don't raycast — they trust heightAt).
      return 0.18;
    }
    if (this.trainRoof) {
      // Flat boxcar rooftops — tops at y=0.18 (gaps have no support mesh, so
      // the player falls through them and the freefall watchdog respawns).
      return 0.18;
    }
    if (this.doorDash) {
      // Solid purple ground sits with its top at y=0.18 (same shape pattern
      // as ice-crack platforms). Keeps ghosts on the visible surface.
      return 0.18;
    }
    if (this.rollingTubes) {
      // Variable height — platforms alternate between a low and high
      // height; tubes are sloped ramps connecting them. The walkable
      // surface for the player follows this height profile.
      const rt = this.rollingTubes;
      for (const p of rt.platforms) {
        if (s >= p.start && s <= p.end) return p.y;
      }
      for (const t of rt.tubes) {
        if (s >= t.start && s <= t.end) {
          const u = (s - t.start) / Math.max(0.001, t.end - t.start);
          return t.startY + (t.endY - t.startY) * u;
        }
      }
      // Fallback (out of range) — use the platform radius.
      return rt.tubes[0]?.radius ?? 3.5;
    }
    if (this.circleSpinner) {
      // Circles + corridors share a uniform walking height (matches the
      // ice-crack/door-dash pattern). Both decor pieces have their tops at
      // y=0.18.
      return 0.18;
    }
    if (this.mazeRush) {
      return 0.18;
    }
    if (this.spaceShip) {
      return 0.18;
    }
    if (this.supermarketSweep) {
      return 0.18;
    }
    if (this.riverBoat) {
      // Docks + boat tops are all at the same physical height as the
      // space-ship pattern: smooth y=0.18 for the path; decor lifts
      // the actual boat decks to 0.73 world via the same ride system.
      return 0.18;
    }
    if (this.blockClimb) {
      // Ramp + plateau profile. Smoothstep within each climb so the
      // height curve eases in/out at the ends and the player walks
      // up cleanly without snap-jumps.
      const bc = this.blockClimb;
      // Inside a flat safe pad?
      for (const p of bc.pads) {
        if (s >= p.start && s <= p.end) return p.y;
      }
      // Inside a climb?
      for (const c of bc.climbs) {
        if (s >= c.start && s <= c.end) {
          const t = (s - c.start) / Math.max(0.0001, c.end - c.start);
          return c.startY + (c.endY - c.startY) * smoothstep(t);
        }
      }
      // Before / after — flat at the nearest endpoint.
      const first = bc.climbs[0];
      if (first && s < first.start) return first.startY;
      const last = bc.climbs[bc.climbs.length - 1];
      if (last && s > last.end) return last.endY;
      return 0.18;
    }
    for (const p of this.platforms) {
      if (s < p.start || s > p.dropEnd) continue;
      if (s < p.rampUpEnd) {
        const t = (s - p.start) / Math.max(0.001, p.rampUpEnd - p.start);
        return p.height * smoothstep(t);
      }
      if (s < p.topEnd) return p.height;
      const t = (s - p.topEnd) / Math.max(0.001, p.dropEnd - p.topEnd);
      return p.height * (1 - smoothstep(t));
    }
    return 0;
  }

  heightSlopeAt(s: number, sampleDistance = 1.2): number {
    const a = this.heightAt(Math.max(0, s - sampleDistance));
    const b = this.heightAt(Math.min(this.length, s + sampleDistance));
    return (b - a) / (sampleDistance * 2);
  }

  tangentAt(s: number, target?: THREE.Vector3): THREE.Vector3 {
    const i = this.indexFor(s);
    const a = this.samples[i]!;
    const b = this.samples[i + 1] ?? a;
    const t = b.s > a.s ? (s - a.s) / (b.s - a.s) : 0;
    const out = target ?? new THREE.Vector3();
    out.copy(a.tan).lerp(b.tan, t).normalize();
    return out;
  }

  /** Y-axis rotation matching the tangent (use as `mesh.rotation.y`). */
  yawAt(s: number): number {
    const tan = this.tangentAt(s, _tmpTan);
    return Math.atan2(tan.x, tan.z);
  }
}

const _tmpTan = new THREE.Vector3();

/** Analytic lane warp profiles for maze-rush. `t` runs 0..1 across a section.
 *  Design intent:
 *    • Lane 0 (LEFT)   = SHORTEST + HARDEST path. Mostly straight (minimal
 *      lateral / vertical sway → small arc length), but loaded up with
 *      obstacles in mazeRushDecor. Quickest route if you survive.
 *    • Lane 1 (CENTER) = MEDIUM length, medium difficulty. Some snaking.
 *    • Lane 2 (RIGHT)  = LONGEST + EASIEST path. Big wide curves arcing
 *      out and high — covers more ground, so a slower-but-safer route.
 *  At t=0 and t=1 the warp is (0, 0) so lanes meet the platform pads flush. */
export function laneWarp(
  sectionIdx: number,
  laneIdx: number,
  t: number,
): { dx: number; dy: number } {
  const sinπt = Math.sin(Math.PI * t);
  const sin2πt = Math.sin(2 * Math.PI * t);
  const ends = [-5.4, 0, 5.4];
  const startX = ends[laneIdx] ?? 0;
  const endX = ends[laneIdx] ?? 0;
  const baseX = startX + (endX - startX) * t;

  // Per-section "flavour" so the choreography varies, while the *length*
  // ranking (lane 0 < lane 1 < lane 2) is preserved across every section.
  const variant = sectionIdx % 3;
  const flavour = variant === 0 ? 1 : variant === 1 ? -1 : 0.6;

  if (laneIdx === 0) {
    // Short + hard — small lateral wobble + a small dip.
    return { dx: baseX + 0.6 * sin2πt * flavour, dy: -0.6 * sinπt };
  }
  if (laneIdx === 1) {
    // Medium — moderate snake, gentle rise.
    return { dx: baseX + 2.2 * sin2πt * flavour, dy: 1.8 * sinπt };
  }
  // Lane 2 — long arcing path that sweeps wide and high.
  return { dx: baseX - 5.0 * sinπt * Math.sign(flavour || 1), dy: 4.2 * sinπt };
}

function smoothstep(t: number): number {
  t = Math.max(0, Math.min(1, t));
  return t * t * (3 - 2 * t);
}
